import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, timeAgo, limitWords } from "@/lib/format";
import { toast } from "sonner";

function ReviewForm({ userId, onSubmitted }) {
  const [rating, setRating] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = Number(rating) >= 1 && Number(rating) <= 10;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await api.post(`/users/${userId}/reviews`, { rating: Number(rating), text });
      setRating("");
      setText("");
      toast.success("Review submitted");
      onSubmitted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 pt-6" style={{ borderTop: "1px solid var(--border)" }}>
      <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Leave a review</label>
      <div className="flex items-center gap-3 mt-3">
        <input
          data-testid="review-rating-input"
          type="text"
          inputMode="decimal"
          value={rating}
          onChange={(e) => {
            const v = e.target.value;
            // allow empty, up to two whole digits, and at most one decimal place
            if (v === "" || /^\d{0,2}(\.\d?)?$/.test(v)) setRating(v);
          }}
          className="w-20 px-3 py-2 text-center text-lg"
          style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text-primary)" }}
        />
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>/ 10</span>
      </div>
      <textarea
        data-testid="review-text-input"
        value={text}
        onChange={(e) => setText(limitWords(e.target.value, text, 1000))}
        rows={3}
        placeholder="your thoughts…"
        className="w-full mt-3 p-3 text-sm resize-none"
        style={{ background: "var(--bg-primary)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
      />
      <button onClick={submit} disabled={!valid || busy} data-testid="review-submit-btn" className="btn w-full py-2 mt-3 text-xs tracking-widest">
        {busy ? "…" : "SUBMIT REVIEW"}
      </button>
    </div>
  );
}

export function ReviewsModal({ userId, onClose, noReview = false }) {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);

  const isSelf = user?.id === userId;
  const isGuest = user?.is_guest;

  const load = useCallback(() => {
    if (!userId) return;
    api.get(`/users/${userId}`).then(({ data }) => setSummary(data));
    api.get(`/users/${userId}/reviews`).then(({ data }) => setReviews(data));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-6 fade-in overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={onClose}
      data-testid="reviews-modal-overlay"
    >
      <div
        className="w-full max-w-md p-8 my-auto"
        style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="reviews-modal"
      >
        <div className="flex justify-between items-start mb-6">
          <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Reviews</span>
          <button onClick={onClose} data-testid="reviews-modal-close" className="text-sm opacity-60 hover:opacity-100" style={{ color: "var(--text-primary)" }}>
            [ close ]
          </button>
        </div>

        <div className="flex items-baseline justify-between pb-6" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-baseline gap-2">
            <span className="font-head text-3xl" style={{ color: "var(--accent)" }} data-testid="reviews-avg">
              {summary && summary.average_rating != null ? summary.average_rating.toFixed(2) : "—"}
            </span>
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>/ 10 avg</span>
          </div>
          <span className="text-xs tracking-widest uppercase" style={{ color: "var(--text-muted)" }} data-testid="reviews-count">
            {summary ? summary.review_count : 0} {summary && summary.review_count === 1 ? "review" : "reviews"}
          </span>
        </div>

        {!isSelf && !isGuest && !noReview && summary?.can_review && <ReviewForm userId={userId} onSubmitted={load} />}
        {isGuest && !isSelf && !noReview && !summary?.is_god && (
          <p className="text-xs mt-4" style={{ color: "var(--text-muted)" }}>Sign in to leave a review.</p>
        )}

        <div className="mt-6 flex flex-col gap-4">
          {reviews.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="reviews-empty">No reviews yet.</p>
          ) : (
            reviews.map((r) => (
              <div key={r.id} data-testid={`review-item-${r.id}`} className="pl-4" style={{ borderLeft: "1px solid var(--border)" }}>
                <div className="flex items-center gap-2">
                  <span className="font-head text-base" style={{ color: "var(--accent)" }}>{r.rating}/10</span>
                  <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{r.reviewer?.name || "unknown"}</span>
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>· {timeAgo(r.created_at)}</span>
                </div>
                {r.text && <p className="long-text-sm text-sm mt-1" style={{ color: "var(--text-primary)" }}>{r.text}</p>}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
